import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/**
 * One participant's day-by-day record plus their motivational totals -- a counter per level, the
 * freeze, and completed weeks (SPECIFIKACE.md ch. 6, PLAN-STREAK.md ch. 6). Reached by tapping a
 * leaderboard row.
 *
 * The day table comes from cv_streak_history rather than the daily summaries, because the days
 * that decide a streak are precisely the ones with nothing in them: a skipped day leaves no entry
 * behind, so a table built from entries can only ever show the good news.
 */
@Composable
fun HistoryScreen(token: String, participantId: String) {
    val state = rememberAsyncState()
    var participantName by remember { mutableStateOf<String?>(null) }
    var days by remember { mutableStateOf<List<StreakDay>>(emptyList()) }
    var streak by remember { mutableStateOf<Streak?>(null) }
    var weeks by remember { mutableStateOf<CompletedWeeks?>(null) }
    var totalPoints by remember { mutableStateOf(0.0) }
    var medals by remember { mutableStateOf<MedalTotals?>(null) }

    LaunchedEffect(participantId) {
        state.run {
            participantName = Api.fetchParticipant(participantId)?.name
            days = Api.fetchStreakHistory(token, participantId)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            weeks = Api.fetchCompletedWeeks(token, participantId).firstOrNull()
            totalPoints = Api.fetchLeaderboard(token, participantId).firstOrNull()?.totalPoints ?: 0.0
            medals = Api.fetchMedalTotals(token, participantId).firstOrNull()
        }
    }

    Div {
        H3 { Text(participantName ?: "…") }
        ErrorMessage(state.error)

        if (!state.loaded) {
            LoadingText()
            return@Div
        }

        Div(attrs = { classes("stats-row") }) {
            StatCard("Body celkem", formatNumber(totalPoints))
            val medalSummary = medalSummaryText(medals)
            if (medalSummary.isNotEmpty()) StatCard("Medaile z výzev", medalSummary)
            // Every tracked level gets a card, including the ones sitting at zero: the all-time
            // best is worth seeing even when the run behind it is long gone.
            streak?.levels?.forEach { StreakLevelCard(it, pending = streak?.pending == true) }
            streak?.let { if (it.freezeEnabled) FreezeCard(it) }
            StatCard("Dokončené týdny", "🛡️ ${weeks?.completedWeeksTotal ?: 0}")
        }

        if (days.isEmpty()) {
            P { Text("Zatím žádné zápisy.") }
            return@Div
        }

        Table {
            Thead {
                Tr {
                    Th { Text("Datum") }
                    Th { Text("Body") }
                    Th { Text("Hodnocení") }
                    Th { Text("Stav") }
                }
            }
            Tbody {
                collapseGaps(days).forEach { row ->
                    when (row) {
                        is HistoryRow.Logged -> DayRow(row.day)
                        is HistoryRow.Gap -> GapRow(row)
                    }
                }
            }
        }
    }
}

@Composable
private fun StreakLevelCard(level: StreakLevel, pending: Boolean) {
    val icon = when {
        level.streak == 0 -> ""
        pending -> "❄ "
        else -> "🔥 "
    }
    Div(attrs = { classes("stat-card") }) {
        Div(attrs = { classes("stat-value", streakBadgeClass(level.label)) }) { Text("$icon${level.streak}") }
        Div(attrs = { classes("stat-label") }) { Text(level.label) }
        Div(attrs = { classes("stat-note") }) { Text("nejlepší ${level.best}") }
    }
}

@Composable
private fun FreezeCard(streak: Streak) {
    Div(attrs = { classes("stat-card") }) {
        Div(attrs = { classes("stat-value") }) { Text("❄ ${streak.freezeBalance}") }
        Div(attrs = { classes("stat-label") }) { Text("Zmrazení") }
        Div(attrs = { classes("stat-note") }) {
            Text(
                if (streak.freezeBalance > 0) {
                    "použito ${streak.frozenDaysTotal}×, zachránilo ${streak.freezeRepaidTotal}×"
                } else {
                    "${streak.freezeProgress}/${streak.freezeEarnDays} dní k dalšímu"
                },
            )
        }
    }
}

@Composable
private fun DayRow(day: StreakDay) {
    Tr(attrs = {
        when {
            day.tierLabel == "Nemocný" -> classes("day-row-illness")
            day.status == "missed" -> classes("day-row-missed")
        }
    }) {
        Td { Text("${formatCzechDate(day.day)} · ${shortDayNameFor(day.day)}") }
        Td { Text(if (day.points > 0) formatNumber(day.points) else "–") }
        Td { day.tierLabel?.let { TierBadge(it) } }
        Td {
            when {
                day.tierLabel == "Nemocný" -> Span(attrs = { classes("day-illness") }) { Text("nemoc") }
                day.status == "frozen" -> Span(attrs = { classes("day-frozen") }) { Text("❄ zmrazeno") }
                day.status == "missed" -> Span(attrs = { classes("day-missed") }) {
                    Text(if (day.points > 0) "nesplněno" else "nezapsáno")
                }
                day.status == "today" -> Span(attrs = { classes("day-today") }) { Text("dnes") }
                day.repaid -> Span(attrs = { classes("day-repaid") }) { Text("✓ doplaceno") }
                else -> {}
            }
        }
    }
}

@Composable
private fun GapRow(gap: HistoryRow.Gap) {
    Tr(attrs = { classes("day-row-missed") }) {
        Td { Text(formatCzechDateRange(gap.fromDate, gap.toDate)) }
        Td { Text("–") }
        Td {}
        Td { Span(attrs = { classes("day-missed") }) { Text("${czechDays(gap.days)} nezapsáno") } }
    }
}

/** A row of the history table: either one real day or a run of days nobody logged. */
private sealed interface HistoryRow {
    data class Logged(val day: StreakDay) : HistoryRow
    data class Gap(val fromDate: String, val toDate: String, val days: Int) : HistoryRow
}

/**
 * Folds runs of untouched days into one row.
 *
 * Without this, somebody who stopped training six months ago opens their history to two hundred
 * identical "nezapsáno" rows before reaching anything they did. A day logged but too weak to count
 * still gets its own row -- that is a real entry, not an absence. [days] arrives newest first.
 */
private fun collapseGaps(days: List<StreakDay>): List<HistoryRow> {
    val untouched = { day: StreakDay ->
        day.status == "missed" && day.points == 0.0 && day.tierLabel != "Nemocný"
    }
    val rows = mutableListOf<HistoryRow>()
    var index = 0
    while (index < days.size) {
        if (!untouched(days[index])) {
            rows += HistoryRow.Logged(days[index])
            index++
            continue
        }
        var end = index
        while (end + 1 < days.size && untouched(days[end + 1])) end++
        val length = end - index + 1
        rows += if (length > 1) {
            HistoryRow.Gap(fromDate = days[end].day, toDate = days[index].day, days = length)
        } else {
            HistoryRow.Logged(days[index])
        }
        index = end + 1
    }
    return rows
}
